import { supabase } from '@/lib/integrations/supabase'

export type WhatsAppMessageType = 'charge' | 'duedate_alert'
export type WhatsAppMessageStatus = 'sent' | 'failed'

export interface WhatsAppMessage {
  id: string
  party_id: string
  entry_id: string
  unit_id: string
  type: WhatsAppMessageType
  message_content: string
  phone: string
  sent_at: string
  status: WhatsAppMessageStatus
  error_message: string | null
  created_at: string
}

export interface LogWhatsAppMessageInput {
  party_id: string
  entry_id: string
  unit_id: string
  type: WhatsAppMessageType
  message_content: string
  phone: string
  status?: WhatsAppMessageStatus
  error_message?: string | null
}

export async function logWhatsAppMessage(input: LogWhatsAppMessageInput): Promise<WhatsAppMessage> {
  const { data, error } = await supabase
    .from('whatsapp_messages')
    .insert({
      party_id: input.party_id,
      entry_id: input.entry_id,
      unit_id: input.unit_id,
      type: input.type,
      message_content: input.message_content,
      phone: input.phone,
      status: input.status ?? 'sent',
      error_message: input.error_message ?? null,
    })
    .select()
    .single()
  if (error) throw error
  return data as WhatsAppMessage
}

export async function getMessagesForEntry(entryId: string): Promise<WhatsAppMessage[]> {
  const { data, error } = await supabase
    .from('whatsapp_messages')
    .select('*')
    .eq('entry_id', entryId)
    .order('sent_at', { ascending: false })
  if (error) throw error
  return (data as WhatsAppMessage[]) || []
}

export async function getMessagesForParty(partyId: string, unitId: string): Promise<WhatsAppMessage[]> {
  const { data, error } = await supabase
    .from('whatsapp_messages')
    .select('*')
    .eq('party_id', partyId)
    .eq('unit_id', unitId)
    .order('sent_at', { ascending: false })
  if (error) throw error
  return (data as WhatsAppMessage[]) || []
}

export async function getMessagesForPartyToday(
  partyId: string,
  unitId: string,
  type: WhatsAppMessageType,
): Promise<WhatsAppMessage[]> {
  const today = new Date().toISOString().split('T')[0]
  const { data, error } = await supabase
    .from('whatsapp_messages')
    .select('*')
    .eq('party_id', partyId)
    .eq('unit_id', unitId)
    .eq('type', type)
    .gte('sent_at', `${today}T00:00:00`)
    .lt('sent_at', `${today}T23:59:59`)
    .order('sent_at', { ascending: false })
  if (error) throw error
  return (data as WhatsAppMessage[]) || []
}

export async function countMessagesForEntry(
  entryId: string,
  type?: WhatsAppMessageType,
): Promise<number> {
  let query = supabase
    .from('whatsapp_messages')
    .select('*', { count: 'exact', head: true })
    .eq('entry_id', entryId)

  if (type) query = query.eq('type', type)

  const { count, error } = await query
  if (error) throw error
  return count ?? 0
}
